import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Bot } from 'lucide-react';
import { useDmdilly } from './dmdilly-context';
import { startCreatorInterview, replyToInterview } from './api';
import ChatMessage from './ui/chat-message';
import ChatInput from './ui/chat-input';
import LoadingSpinner from './ui/loading-spinner';
import ErrorMessage from './ui/error-message';

export default function CreatorInterview({ onComplete, creatorIdOverride }) {
  const { apiBaseUrl, creatorId: contextCreatorId } = useDmdilly();
  const creatorId = creatorIdOverride || contextCreatorId;

  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState(null);
  const [complete, setComplete] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    initInterview();
  }, [creatorId]);

  const initInterview = async () => {
    setInitializing(true);
    setError(null);

    const { data, error: apiError } = await startCreatorInterview(apiBaseUrl, creatorId);

    if (apiError) {
      setError(apiError);
      setInitializing(false);
      return;
    }

    console.log('Interview started:', data.data);

    setSessionId(data.data.sessionId);
    if(data.data.question) {
      setMessages([{ role: 'ai', content: data.data.question }]);
    }
    setInitializing(false);
  };

  const handleSubmit = async (message) => {
    setMessages((prev) => [...prev, { role: 'user', content: message }]);
    setLoading(true);

    const { data, error: apiError } = await replyToInterview(apiBaseUrl, sessionId, message);

    if (apiError) {
      setError(apiError);
      setLoading(false);
      return;
    }

    if (data.data.isComplete) {
      setComplete(true);
      setMessages((prev) => [
        ...prev,
        {
          role: 'ai',
          content:
            "Thanks! I've captured your current focus. Your preferences are now saved and will be used to prioritize incoming messages.",
        },
      ]);
      if (onComplete) {
        setTimeout(() => onComplete(data.data.profile), 1500);
      }
    } else {
      setMessages((prev) => [...prev, { role: 'ai', content: data.data.latestMessage }]);
    }

    setLoading(false);
  };

  if (initializing) {
    return <LoadingSpinner message="Starting interview..." />;
  }

  if (error && messages.length === 0) {
    return <ErrorMessage message={error} onRetry={initInterview} />;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-gray-100">
        <div className="w-10 h-10 rounded-full bg-boggler-teal/10 flex items-center justify-center">
          <Bot size={20} className="text-boggler-teal" />
        </div>
        <div>
          <h2 className="font-semibold text-gray-800">Intent Interview</h2>
          <p className="text-xs text-gray-500">Tell me about the type of clients you would like to connect with and for what purpose.</p>
        </div>
      </div>

      {/* Messages */}
     {messages && messages.length > 0 && <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, index) => (
          <ChatMessage key={index} role={msg.role} content={msg.content} />
        ))}

        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex gap-2 items-center text-gray-500 text-sm pl-11"
          >
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-boggler-teal rounded-full animate-bounce" />
              <span
                className="w-2 h-2 bg-boggler-teal rounded-full animate-bounce"
                style={{ animationDelay: '0.1s' }}
              />
              <span
                className="w-2 h-2 bg-boggler-teal rounded-full animate-bounce"
                style={{ animationDelay: '0.2s' }}
              />
            </div>
            <span>Thinking...</span>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>}

      {/* Input */}
      {!complete && (
        <div className="p-4 border-t border-gray-100">
          <ChatInput
            onSubmit={handleSubmit}
            disabled={loading || complete}
            loading={loading}
            placeholder="Type your answer..."
          />
        </div>
      )}

      {complete && (
        <div className="p-4 border-t border-gray-100 bg-green-50">
          <p className="text-center text-green-600 text-sm font-medium">
            ✓ Interview complete! Your preferences have been saved.
          </p>
        </div>
      )}
    </div>
  );
}
